import {
  take,
  fork,
  cancel as cancelEffect,
  race,
  call,
  put,
  cancelled as cancelledEffect,
} from 'redux-saga/effects';
import {delay} from 'redux-saga';

/* Constants */
import {
  ACTION_NAME_DELIMITER,
  ASYNC_ACTION_SUFFIXES,
  ASYNC_ACTION_TYPES,
  ASYNC_ACTION_PHASES,
} from './reduxConstants';

function stripAsyncActionNameSuffixes(actionName) {
  const partials = actionName.split(ACTION_NAME_DELIMITER);
  const suffix = partials[partials.length - 1];
  const hasAsyncSuffix =
    suffix === ASYNC_ACTION_SUFFIXES.FULFILLED ||
    suffix === ASYNC_ACTION_SUFFIXES.REJECTED ||
    suffix === ASYNC_ACTION_SUFFIXES.CANCELLED ||
    suffix === ASYNC_ACTION_SUFFIXES.CLEANUP;
  let baseActionName;

  if (hasAsyncSuffix) {
    baseActionName = partials.slice(0, partials.length - 1).join(ACTION_NAME_DELIMITER);
  } else {
    baseActionName = actionName;
  }

  return {
    baseActionName,
    suffix,
  };
}

function generateAsyncActionNames(baseActionName) {
  return {
    triggerActionName: baseActionName,
    fulfilledActionName: baseActionName + ACTION_NAME_DELIMITER + ASYNC_ACTION_SUFFIXES.FULFILLED,
    rejectedActionName: baseActionName + ACTION_NAME_DELIMITER + ASYNC_ACTION_SUFFIXES.REJECTED,
    cancelledActionName: baseActionName + ACTION_NAME_DELIMITER + ASYNC_ACTION_SUFFIXES.CANCELLED,
    cleanupActionName: baseActionName + ACTION_NAME_DELIMITER + ASYNC_ACTION_SUFFIXES.CLEANUP,
  };
}

function generateDefaultAsyncWatcher(baseActionName, asyncSaga) {
  const {
    triggerActionName,
    fulfilledActionName,
    rejectedActionName,
    cancelledActionName,
  } = generateAsyncActionNames(baseActionName);

  return function* () {
    while (true) {
      const triggerAction = yield take(triggerActionName);
      const task = yield fork(asyncSaga, triggerAction);
      const resultAction = yield take([
        triggerActionName,
        fulfilledActionName,
        rejectedActionName,
        cancelledActionName,
      ]);

      if (resultAction.type === cancelledActionName) {
        yield cancelEffect(task);
      }
    }
  };
}

function generateDefaultPollingWatcher(baseActionName, pollingSaga) {
  const {triggerActionName, cancelledActionName} = generateAsyncActionNames(baseActionName);

  return function* () {
    while (true) {
      const triggerAction = yield take(triggerActionName);

      yield race([
        call(pollingSaga, triggerAction),
        take(cancelledActionName),
      ]);
    }
  };
}

function generateAsyncActionCreators(
  baseActionName,
  payloadGenerator,
  asyncType = ASYNC_ACTION_TYPES.BASIC,
) {
  const {
    triggerActionName,
    fulfilledActionName,
    rejectedActionName,
    cancelledActionName,
    cleanupActionName,
  } = generateAsyncActionNames(baseActionName);

  function trigger(...args) {
    return {
      type: triggerActionName,
      payload: payloadGenerator ? payloadGenerator(...args) : {},
      isAsync: true,
      asyncType,
      asyncPhase: ASYNC_ACTION_PHASES.TRIGGER,
      pendingResult: true,
    };
  }

  function fulfill(response, triggerPayload) {
    return {
      type: fulfilledActionName,
      payload: {
        response,
      },
      triggerPayload,
      isAsync: true,
      asyncType,
      asyncPhase: ASYNC_ACTION_PHASES.FULFILL,
      pendingResult: false,
    };
  }


  function reject(error, triggerPayload) {
    return {
      type: rejectedActionName,
      payload: {
        error,
      },
      triggerPayload,
      isError: true,
      isAsync: true,
      asyncType,
      asyncPhase: ASYNC_ACTION_PHASES.REJECT,
      pendingResult: false,
    };
  }

  function cancel(triggerPayload) {
    return {
      type: cancelledActionName,
      triggerPayload,
      isAsync: true,
      asyncType,
      asyncPhase: ASYNC_ACTION_PHASES.CANCEL,
      payload: {},
    };
  }

  function cleanup(triggerPayload) {
    return {
      type: cleanupActionName,
      payload: {},
      triggerPayload,
      isAsync: true,
      asyncType,
      asyncPhase: ASYNC_ACTION_PHASES.CLEANUP,
      pendingResult: false,
    };
  }

  return {
    trigger,
    fulfill,
    reject,
    cancel,
    cleanup,
  };
}

function generateDefaultAsyncSaga(
  networkCallHandler,
  actionCreators,
  payloadExtractor = payload => [],
  cacheSettings) {
  return function* (action) {
    const {payload} = action;
    let result;
    try {
      const response = yield call(networkCallHandler, ...payloadExtractor(payload), cacheSettings);
      yield put(actionCreators.fulfill(response, payload));
      result = response;
    } catch (error) {
      yield put(actionCreators.reject(error, payload));
      result = error;
    } finally {
      if (yield cancelledEffect()) {
        yield put(actionCreators.cleanup(payload));
      }
    }

    return result;
  };
}

function generateDefaultPollingSaga(asyncSaga,
                                    actionCreators,
                                    {interval, stopHandler} = {}) {
  return function* (action) {

    while (true) {
      try {
        const pollResponse = yield call(asyncSaga, action);

        yield put(actionCreators.fulfill(pollResponse));

        if (stopHandler && stopHandler()) {
          return pollResponse;
        }

        yield call(delay, interval);
      } catch (error) {
        yield put(actionCreators.reject(error));
      } finally {
        if (yield cancelledEffect()) {
          yield put(actionCreators.cleanup());
        }
      }
    }
  };
}

const defaultGetOperationSagaWithCacheOptions = {
  shouldUseExistingCachedDataOnError: true,
};

function generateGetOperationSagaWithCache(
  getOperationSaga,
  actionCreators,
  responseExtractor = response => response.data.data,
  options = {},
) {
  const finalOptions = {
    ...defaultGetOperationSagaWithCacheOptions,
    ...options,
  };
  const {
    store,
    storeKeyParam,
    storeKeyParamGenerator,
    shouldUseExistingCachedDataOnError,
  } = finalOptions;

  return function* (action) {
    const {payload} = action;
    let result, isDataFromCache, cachedData, resultData;

    try {
      cachedData = store.load(storeKeyParam || storeKeyParamGenerator(action.payload));

      if (cachedData) {
        resultData = cachedData;
        isDataFromCache = true;
      } else {
        const getSagaResult = yield getOperationSaga(action);

        resultData = responseExtractor(getSagaResult);
        isDataFromCache = false;
      }

      result = {
        resultData,
        isDataFromCache,
      };

      yield put(actionCreators.fulfill(result, payload));
    } catch (error) {
      let cachedData;

      if (shouldUseExistingCachedDataOnError &&
        (cachedData = store.loadWithoutExpiration(storeKeyParam || storeKeyParamGenerator(action.payload)))) {
        result = {
          resultData: cachedData,
          isDataFromCache: true,
        };

        yield put(actionCreators.fulfill(result, payload));
      } else {
        result = error;
        yield put(actionCreators.reject(error, payload));
      }
    } finally {
      if (yield cancelledEffect()) {
        yield put(actionCreators.cleanup(payload));
      }
    }

    return result;
  };
}

export {
  stripAsyncActionNameSuffixes,
  generateDefaultAsyncWatcher,
  generateDefaultPollingWatcher,
  generateAsyncActionNames,
  generateAsyncActionCreators,
  generateDefaultAsyncSaga,
  generateDefaultPollingSaga,
  generateGetOperationSagaWithCache,
};
